import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { InputAdornment, IconButton } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";

const blockedInputProps = {
  readOnly: true,
  onKeyDown: (e: React.KeyboardEvent) => e.preventDefault(),
  onKeyPress: (e: React.KeyboardEvent) => e.preventDefault(),
  onKeyUp: (e: React.KeyboardEvent) => e.preventDefault(),
  onInput: (e: React.FormEvent) => e.preventDefault(),
  onBeforeInput: (e: any) => e.preventDefault(),
  onPaste: (e: React.ClipboardEvent) => e.preventDefault(),
  onCut: (e: React.ClipboardEvent) => e.preventDefault(),
  onChange: (e: React.ChangeEvent) => e.preventDefault(),
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.blur(), // ✅ prevents format mask from showing
  style: {
    caretColor: "transparent",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    cursor: "pointer",
  },
} as any;

const blockedInputSx = {
  "& .MuiInputBase-root": {
    cursor: "pointer !important", //  changed from "default" to "pointer"
  },
  "& .MuiInputBase-input": {
    // ❌ REMOVED: pointerEvents: "none !important" — was blocking click/touch
    caretColor: "transparent !important",
    userSelect: "none !important",
    WebkitUserSelect: "none !important",
    MozUserSelect: "none !important",
    cursor: "pointer !important", //  changed from default
  },
  "& .MuiIconButton-root": {
    pointerEvents: "auto !important",
    cursor: "pointer !important",
  },
};
type timeFrameProps = {
  value: [any, any];
  onChange: (value: [any, any]) => void;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  dataTracker?: string;
  isDisabled?: boolean;
  maxDate?: Dayjs;
};

const TimeFrameDateRange = ({
  value,
  onChange,
  label = "Time Period",
  placeholder = "Select Time Period",
  dataTracker,
  isDisabled,
  maxDate,
}: timeFrameProps) => {
  const [tempValue, setTempValue] = useState<[any, any]>(value);
  const [open, setOpen] = useState(false);
  const tempValueRef = React.useRef<[any, any]>(value);

  useEffect(() => {
    setTempValue(value);
    tempValueRef.current = value;
  }, [value]);

  const hasCommittedRange = !!(value?.[0] && value?.[1]);
  const hasTempRange = !!(tempValue?.[0] && tempValue?.[1]);
  const showClearIcon = hasCommittedRange || hasTempRange;

  // When opening with no committed range, pre-select MTD in the picker
  const handleOpen = () => {
    // if (!hasCommittedRange) {
    //   const [mtdStart, mtdEnd] = getMTDRange();
    //   setTempValue([mtdStart, mtdEnd]);
    //   tempValueRef.current = [mtdStart, mtdEnd];
    // }
    setOpen(true);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTempValue([null, null]);
    tempValueRef.current = [null, null];
    onChange([null, null]);
    setOpen(false); // close picker if open
  };

  const handleClose = () => {
    setOpen(false);
    const [start, end] = tempValueRef.current || [];

    if (start && end) {
      const prevStart = value?.[0];
      const prevEnd = value?.[1];
      const isSame =
        prevStart &&
        prevEnd &&
        dayjs(start).isSame(dayjs(prevStart), "day") &&
        dayjs(end).isSame(dayjs(prevEnd), "day");

      if (!isSame) {
        onChange([start, end]);
      }
    } else {
      setTempValue(value);
      tempValueRef.current = value;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateRangePicker
        disabled={isDisabled}
        open={open}
        onOpen={handleOpen} //replaced setOpen(true) with handleOpen
        onClose={handleClose}
        slots={{ field: SingleInputDateRangeField }}
        value={tempValue}
        onChange={(newValue) => {
          setTempValue(newValue as [any, any]);
          tempValueRef.current = newValue as [any, any];
        }}
        maxDate={maxDate}
        format="DD/MM/YYYY"
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            //required: isRequired,
            color: "secondary",
            inputProps: blockedInputProps,
            sx: blockedInputSx,
            placeholder,
            InputLabelProps: { shrink: true },
            label,
            InputProps: {
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: "2px" }}>
                  {showClearIcon && (
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      disabled={isDisabled}
                      sx={{
                        padding: "2px",
                        color: isDisabled ? "text.disabled" : "text.secondary",
                        cursor: isDisabled ? "default" : "pointer",
                        pointerEvents: isDisabled ? "none" : "auto",
                        "&:hover": {
                          color: isDisabled
                            ? "text.disabled"
                            : "secondary.main",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  <CalendarMonthIcon
                    fontSize="medium"
                    onClick={() => !isDisabled && handleOpen()} // use handleOpen
                    sx={{
                      cursor: isDisabled ? "default" : "pointer",
                      pointerEvents: isDisabled ? "none" : "auto",
                      "&:hover": { color: "secondary.main" },
                    }}
                  />
                </InputAdornment>
              ),
            },
          },
          popper: {
            ...({
              "data-tracker": dataTracker,
            } as React.HTMLAttributes<HTMLDivElement>),
          },
        }}
      />
    </LocalizationProvider>
  );
};
export default TimeFrameDateRange;
